import { useEffect, useState } from "react";
import { Save, Check, X, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminGetMobileAuth, adminPutMobileAuth, adminMobileApiContractBlob, adminMobileApiContractMeta } from "@/lib/api";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

export default function AdminMobileAuth() {
  const [s, setS] = useState(null);
  const [googleIds, setGoogleIds] = useState("");
  const [apple, setApple] = useState({ apple_service_id: "", apple_team_id: "", apple_key_id: "", apple_private_key: "" });
  const [dl, setDl] = useState(false);
  const [contractMeta, setContractMeta] = useState(null);

  const load = () => adminGetMobileAuth().then((d) => {
    setS(d);
    setGoogleIds((d.google_client_ids || []).join("\n"));
    setApple({ apple_service_id: d.apple_service_id || "", apple_team_id: d.apple_team_id || "", apple_key_id: d.apple_key_id || "", apple_private_key: "" });
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { adminMobileApiContractMeta().then(setContractMeta).catch(() => {}); }, []);

  const saveGoogle = async () => {
    const ids = googleIds.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
    await adminPutMobileAuth({ google_client_ids: ids });
    toast.success(ids.length ? "Google sign-in is now live" : "Google client IDs cleared");
    load();
  };
  const saveApple = async () => {
    const payload = { apple_service_id: apple.apple_service_id, apple_team_id: apple.apple_team_id, apple_key_id: apple.apple_key_id };
    if (apple.apple_private_key.trim()) payload.apple_private_key = apple.apple_private_key.trim();
    await adminPutMobileAuth(payload);
    toast.success(apple.apple_service_id ? "Apple sign-in is now live" : "Apple settings saved");
    load();
  };

  const Badge = ({ on }) => (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${on ? "bg-green-500/10 text-green-600" : "bg-surface-alt text-ink-3"}`}>
      {on ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}{on ? "Live" : "Off"}
    </span>
  );

  const downloadContract = async () => {
    setDl(true);
    try {
      const blob = await adminMobileApiContractBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "MOBILE_API_CONTRACT.md";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded MOBILE_API_CONTRACT.md");
    } catch (e) {
      toast.error("Download failed", { description: e.response?.status === 404 ? "Contract file not found" : (e.message || "Try again") });
    } finally { setDl(false); }
  };

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="admin-mobile-auth">
      <h1 className="mb-1 text-2xl font-semibold text-ink">Mobile Sign-in Keys</h1>
      <p className="mb-6 text-sm text-ink-3">Paste your Google and Apple credentials to turn on social sign-in in the mobile app. Changes go live immediately — no redeploy. Users are linked to existing accounts by email.</p>

      <div className="mb-6 flex flex-col items-start gap-3 border border-saffron/40 bg-[var(--saffron)]/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="mobile-contract-card">
        <div>
          <h2 className="font-medium text-ink">Mobile API Contract</h2>
          {contractMeta && (contractMeta.version || contractMeta.updated_at) && (
            <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="mobile-contract-meta">
              {contractMeta.version && <span className="rounded-full bg-saffron/15 px-2 py-0.5 text-[11px] font-medium text-saffron">{contractMeta.version}</span>}
              {contractMeta.updated_at && <span className="text-[11px] text-ink-3">Last updated {contractMeta.updated_at}</span>}
            </div>
          )}
          <p className="mt-1 max-w-xl text-xs text-ink-3">The complete, consolidated API &amp; data contract for your mobile developers (routes, payloads, DB schema, consent rules). Private — hand it over directly, don't publish it.</p>
        </div>
        <button onClick={downloadContract} disabled={dl} data-testid="download-mobile-contract" className="inline-flex shrink-0 items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Download .md
        </button>
      </div>

      <div className="mb-6 border border-line bg-surface p-5">
        <div className="mb-2 flex items-center justify-between"><h2 className="font-medium text-ink">Google Sign-in</h2><Badge on={s?.google_enabled} /></div>
        <p className="mb-2 text-xs text-ink-3">OAuth client IDs that the mobile app presents (one per line — iOS, Android, Web).</p>
        <textarea value={googleIds} onChange={(e) => setGoogleIds(e.target.value)} rows={3} placeholder="1234-abc.apps.googleusercontent.com" data-testid="google-client-ids" className={inp} />
        <button onClick={saveGoogle} data-testid="save-google" className="mt-3 inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white"><Save className="h-4 w-4" />Save & go live</button>
      </div>

      <div className="border border-line bg-surface p-5">
        <div className="mb-2 flex items-center justify-between"><h2 className="font-medium text-ink">Sign in with Apple</h2><Badge on={s?.apple_enabled} /></div>
        <div className="grid gap-3 sm:grid-cols-3">
          <input value={apple.apple_service_id} onChange={(e) => setApple({ ...apple, apple_service_id: e.target.value })} placeholder="Service ID (aud)" data-testid="apple-service-id" className={inp} />
          <input value={apple.apple_team_id} onChange={(e) => setApple({ ...apple, apple_team_id: e.target.value })} placeholder="Team ID" className={inp} />
          <input value={apple.apple_key_id} onChange={(e) => setApple({ ...apple, apple_key_id: e.target.value })} placeholder="Key ID" className={inp} />
        </div>
        <textarea value={apple.apple_private_key} onChange={(e) => setApple({ ...apple, apple_private_key: e.target.value })} rows={3} placeholder={s?.apple_key_set ? "•••• key on file — paste to replace ••••" : "-----BEGIN PRIVATE KEY-----"} className={`${inp} mt-3 font-mono text-xs`} />
        <button onClick={saveApple} data-testid="save-apple" className="mt-3 inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white"><Save className="h-4 w-4" />Save & go live</button>
      </div>
    </div>
  );
}
